import {aiPromptBoxInitialState} from '@Components/ai-prompt-box/ai-prompt-box-library';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {
  AiPromptBoxAddAttachmentsAction,
  AiPromptBoxAddLoadingAttachmentsAction,
  AiPromptBoxAddPendingAttachmentsAction,
  AiPromptBoxInitializationAction,
  AiPromptBoxMarkPendingAttachmentFailedAction,
  AiPromptBoxPendingAttachmentAction,
  AiPromptBoxReduxState,
  AiPromptBoxRemoveImageAttachmentsAction,
  AiPromptBoxRemoveLoadingAttachmentsAction,
  AiPromptBoxResolvePendingAttachmentAction,
  AiPromptBoxPreFillAction,
  AiPromptBoxUpdateAction,
} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {getAttachmentUniqueKey, MAX_ATTACHMENTS} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {PendingCapableAttachment, PendingUpload, PromptAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PendingUploadStatus, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {isUserImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {isPendingUploadAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {deepAssign} from '@Utils/misc.util';

const deduplicateAttachments = (attachments: PromptAttachment[]): PromptAttachment[] => {
  const seenKeys = new Set<string>();

  return attachments.filter((attachment) => {
    const key = getAttachmentUniqueKey(attachment);

    if (seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    return true;
  });
};

const aiPromptBox = createSlice({
  name: 'aiPromptBox',
  initialState: aiPromptBoxInitialState(),
  reducers: {
    initializeAiPromptBox: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxInitializationAction>): void => {
      const {id} = action.payload;
      state[id] = action.payload.value;
    },
    updateAiPromptBox: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxUpdateAction>): void => {
      const {id, value} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      deepAssign(entity, value);
    },
    preFillPromptIfUntouched: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxPreFillAction>): void => {
      const {id, prompt} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      const isUntouched = entity.prompt.trim() === '' || entity.prompt === entity.lastPreFilledPrompt;
      if (!isUntouched) {
        return;
      }

      entity.prompt = prompt;
      entity.lastPreFilledPrompt = prompt;
      // A box with pre-typed text should present as engaged, not resting — but never override a busy/disabled state.
      if (entity.promptBoxState === PromptBoxState.IDLE) {
        entity.promptBoxState = PromptBoxState.ACTIVE;
      }
    },
    addAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddAttachmentsAction>): void => {
      const {id, attachments, loadingAttachments, prefillPromptIfEmpty} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      const currentItems = entity.attachments?.items ?? [];
      const uniqueItems = deduplicateAttachments([...currentItems, ...attachments]).slice(0, entity.attachments?.maxAttachments ?? MAX_ATTACHMENTS);

      entity.attachments = {
        ...entity.attachments,
        items: uniqueItems,
        dropdown: entity.attachments?.dropdown ?? Object.values(PromptAttachmentType),
        maxAttachments: entity.attachments?.maxAttachments,
      };

      if (loadingAttachments !== undefined) {
        entity.attachmentLoaders = loadingAttachments;
      }

      if (prefillPromptIfEmpty !== undefined && entity.prompt.trim() === '') {
        entity.prompt = prefillPromptIfEmpty;
      }
    },
    addLoadingAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddLoadingAttachmentsAction>): void => {
      const {id, loadingAttachments} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      entity.attachmentLoaders = [...(entity.attachmentLoaders ?? []), ...loadingAttachments];
    },
    removeLoadingAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxRemoveLoadingAttachmentsAction>): void => {
      const {id, loadingIds} = action.payload;
      const entity = state[id];

      if (!entity || loadingIds.length === 0) {
        return;
      }

      entity.attachmentLoaders = (entity.attachmentLoaders ?? []).filter((loader) => !loadingIds.includes(loader.id));
    },
    removeImageAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxRemoveImageAttachmentsAction>): void => {
      const {id, hashedFilenames} = action.payload;
      const entity = state[id];

      if (!entity || hashedFilenames.length === 0 || !entity.attachments) {
        return;
      }

      const hashedSet = new Set(hashedFilenames);
      entity.attachments.items = entity.attachments.items.filter((item) => {
        if (!isUserImageAttachment(item)) {
          return true;
        }
        return !hashedSet.has(item.hashedFilename);
      });
    },
    addPendingAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddPendingAttachmentsAction>): void => {
      const {id, attachments} = action.payload;
      const entity = state[id];

      if (!entity || attachments.length === 0) {
        return;
      }

      const currentItems = entity.attachments?.items ?? [];
      const uniqueItems = deduplicateAttachments([...currentItems, ...attachments]).slice(0, entity.attachments?.maxAttachments ?? MAX_ATTACHMENTS);

      entity.attachments = {
        ...entity.attachments,
        items: uniqueItems,
        dropdown: entity.attachments?.dropdown ?? Object.values(PromptAttachmentType),
        maxAttachments: entity.attachments?.maxAttachments,
      };
    },
    resolvePendingAttachment: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxResolvePendingAttachmentAction>): void => {
      const {id, localId, resolved} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      const items = entity.attachments.items;
      const index = findPendingAttachmentIndex(items, localId);
      if (index === -1) {
        return;
      }

      const existing = items[index];
      const wasNonRemovable = isUserImageAttachment(existing) && existing.isRemovable === false;
      items[index] = wasNonRemovable && isUserImageAttachment(resolved) ? {...resolved, isRemovable: false} : resolved;
    },
    markPendingAttachmentFailed: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxMarkPendingAttachmentFailedAction>): void => {
      const pendingUpload = findPendingUpload(state, action.payload);
      if (pendingUpload) {
        pendingUpload.status = PendingUploadStatus.FAILED;
        pendingUpload.failure = action.payload.failure;
      }
    },
    markPendingAttachmentUploading: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxPendingAttachmentAction>): void => {
      const pendingUpload = findPendingUpload(state, action.payload);
      if (pendingUpload) {
        pendingUpload.status = PendingUploadStatus.UPLOADING;
        pendingUpload.failure = undefined;
      }
    },
    markPendingAttachmentProcessing: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxPendingAttachmentAction>): void => {
      const pendingUpload = findPendingUpload(state, action.payload);
      if (pendingUpload) {
        pendingUpload.status = PendingUploadStatus.PROCESSING;
      }
    },
    removePendingAttachment: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxPendingAttachmentAction>): void => {
      const {id, localId} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      entity.attachments.items = entity.attachments.items.filter((item) => {
        return !isPendingUploadAttachment(item) || item.localId !== localId;
      });
    },
  },
});

const findPendingAttachmentIndex = (items: PromptAttachment[], localId: string): number => {
  return items.findIndex((item) => isPendingUploadAttachment(item) && item.localId === localId);
};

const findPendingUploadAttachment = (items: PromptAttachment[], localId: string): PendingCapableAttachment | undefined => {
  const index = findPendingAttachmentIndex(items, localId);
  if (index === -1) {
    return undefined;
  }
  const found = items[index];
  return isPendingUploadAttachment(found) ? found : undefined;
};

const findPendingUpload = (state: AiPromptBoxReduxState, {id, localId}: AiPromptBoxPendingAttachmentAction): PendingUpload | undefined => {
  return findPendingUploadAttachment(state[id]?.attachments?.items ?? [], localId)?.pendingUpload;
};

export const aiPromptBoxReducer = aiPromptBox.reducer;
export const aiPromptBoxActions = aiPromptBox.actions;
